import {
  Business as BusinessIcon,
  CheckCircle as CheckCircleIcon,
  Person as PersonIcon,
  Phone as PhoneIcon,
  Save as SaveIcon,
  Schedule as ScheduleIcon,
  Settings as SettingsIcon,
  TextFields as TextFieldsIcon,
  Warning as WarningIcon,
} from "@mui/icons-material";
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControl,
  InputAdornment,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import React, { useEffect, useState } from "react";
import { useAccount, useToast } from "../../../context";
import { Template } from "../../../interfaces/all";
import { getTemplateParameters } from "../../../services/api";
import { apiService } from "../../../services/axios";
import { ParameterConfig } from "../../../types/template-config";
import TemplatePreview from "./TemplatePreview";

interface TemplateParameterConfiguratorProps {
  template: Template | null;
  isOpen: boolean;
  onClose: () => void;
  onSave: (config: ParameterConfig[]) => void;
}

const TemplateParameterConfigurator: React.FC<
  TemplateParameterConfiguratorProps
> = ({ template, isOpen, onClose, onSave }) => {
  const { showError, showSuccess } = useToast();
  const { account } = useAccount();
  const [loading, setLoading] = useState(false);
  const [templateParameters, setTemplateParameters] = useState<any>(null);
  const [parameterConfigs, setParameterConfigs] = useState<ParameterConfig[]>(
    [],
  );
  const [saving, setSaving] = useState(false);

  // Carregar parâmetros do template quando abrir
  useEffect(() => {
    if (isOpen && template && account?.company_id) {
      loadTemplateParameters();
    }
  }, [isOpen, template, account]);

  const loadPredefinedTemplateConfig = async (templateId: string) => {
    if (!account?.company_id) return null;

    try {
      const response = await apiService.get(
        `/template-configs/${templateId}?companyId=${account.company_id}`,
      );
      if (
        response.status === "success" &&
        response.data &&
        typeof response.data === "object" &&
        response.data !== null &&
        "parameters" in response.data
      ) {
        return (response.data as { parameters: any[] }).parameters || [];
      }
    } catch (error) {
      console.log(
        "Nenhuma configuração pré-definida encontrada para o template:",
        templateId,
      );
    }
    return null;
  };

  const loadTemplateParameters = async () => {
    if (!template || !account?.company_id) {
      return;
    }

    try {
      setLoading(true);

      const predefinedConfig = await loadPredefinedTemplateConfig(template.id);
      const response = await getTemplateParameters(
        template.id,
        template.name,
        account.company_id,
      );

      if (response.status === "success" && response.data) {
        const templateData = response.data as any;
        setTemplateParameters(templateData);

        if (templateData.requiredParametersCount > 0) {
          const configs: ParameterConfig[] = [];

          if (predefinedConfig && predefinedConfig.length > 0) {
            for (let i = 0; i < predefinedConfig.length; i++) {
              const param = predefinedConfig[i];
              let type: "fixed" | "dynamic" = "dynamic";
              let value = "";
              let dynamicField: ParameterConfig["dynamicField"] = "clientName";

              if (typeof param === "object" && param !== null) {
                type = param.type || "dynamic";
                value = param.value || "";
                dynamicField = param.dynamicField;
              } else if (
                typeof param === "string" &&
                param.startsWith("{{") &&
                param.endsWith("}}")
              ) {
                type = "dynamic";
                const placeholderMatch = param.match(/\{\{(\d+)\}\}/);
                if (placeholderMatch) {
                  const placeholderNumber = parseInt(placeholderMatch[1]);
                  switch (placeholderNumber) {
                    case 1:
                      dynamicField = "clientName";
                      break;
                    case 2:
                      dynamicField = "clientPhone";
                      break;
                    case 3:
                      dynamicField = "companyName";
                      break;
                    case 4:
                      dynamicField = "attendantName";
                      break;
                    case 5:
                      dynamicField = "scheduledDate";
                      break;
                    default:
                      dynamicField = "clientName";
                  }
                }
              } else if (typeof param === "string") {
                type = "fixed";
                value = param;
              }

              configs.push({
                index: i,
                type,
                dynamicField,
                value,
              });
            }
          } else {
            for (let i = 0; i < templateData.requiredParametersCount; i++) {
              configs.push({
                index: i,
                type: "dynamic",
                value: "",
                dynamicField: "clientName",
              });
            }
          }

          setParameterConfigs(configs);
        } else {
          setParameterConfigs([]);
        }
      }
    } catch (error) {
      console.error("Erro ao carregar parâmetros do template:", error);
      showError("Erro ao carregar parâmetros do template");
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    if (!template || !account?.company_id) {
      showError("Erro: Dados do usuário ou template não encontrados");
      return;
    }

    try {
      setSaving(true);

      const configData = {
        templateId: template.id,
        templateName: template.name,
        parameters: parameterConfigs.map((config) => ({
          index: config.index,
          type: config.type,
          value: config.value || "",
          dynamicField:
            config.type === "fixed"
              ? null
              : (config.dynamicField ?? "clientName"),
        })),
      };

      await apiService.post("/template-configs/save", {
        templateId: template.id,
        templateName: template.name,
        companyId: account.company_id,
        parameters: configData.parameters,
      });

      showSuccess("Configuração de parâmetros salva com sucesso!");
      onSave(parameterConfigs);
      onClose();
    } catch (error) {
      console.error("Erro ao salvar configuração:", error);
      showError("Erro ao salvar configuração de parâmetros");
    } finally {
      setSaving(false);
    }
  };

  const handleParameterChange = (
    index: number,
    field: keyof ParameterConfig,
    value: any,
  ) => {
    const newConfigs = [...parameterConfigs];
    newConfigs[index] = { ...newConfigs[index], [field]: value };
    setParameterConfigs(newConfigs);
  };

  // Função para validar se um parâmetro está configurado
  const isParameterConfigured = (config: ParameterConfig) => {
    if (config.type === "fixed") {
      return config.value && config.value.trim().length > 0;
    } else {
      return (
        config.dynamicField &&
        config.dynamicField !== null &&
        config.dynamicField !== undefined
      );
    }
  };

  // Função para obter status de validação
  const getValidationStatus = (config: ParameterConfig) => {
    if (isParameterConfigured(config)) {
      return { valid: true, message: "Configurado" };
    } else {
      return { valid: false, message: "Necessário configurar" };
    }
  };

  if (!template) return null;

  return (
    <Dialog
      open={isOpen}
      onClose={onClose}
      maxWidth="lg"
      fullWidth
      slotProps={{
        paper: {
          sx: {
            borderRadius: "12px",
            boxShadow: "0 8px 32px rgba(0, 0, 0, 0.12)",
            overflow: "hidden",
          },
        },
      }}
    >
      <DialogTitle
        sx={{
          p: 3,
          borderBottom: "1px solid #e9edef",
          background: "linear-gradient(135deg, #f8f9fa 0%, #ffffff 100%)",
          position: "relative",
        }}
      >
        {/* Elemento decorativo sutil */}
        <Box
          sx={{
            position: "absolute",
            top: 0,
            right: 0,
            width: 120,
            height: 120,
            background:
              "radial-gradient(circle, rgba(0, 168, 132, 0.05) 0%, transparent 70%)",
            borderRadius: "50%",
            transform: "translate(30px, -30px)",
          }}
        />

        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 2,
            position: "relative",
            zIndex: 1,
          }}
        >
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              width: 48,
              height: 48,
              borderRadius: "12px",
              backgroundColor: "#e3f2fd",
              boxShadow: "0 2px 8px rgba(0, 0, 0, 0.1)",
              border: "1px solid rgba(255, 255, 255, 0.8)",
            }}
          >
            <SettingsIcon sx={{ color: "#1976d2", fontSize: 24 }} />
          </Box>
          <Box sx={{ flex: 1 }}>
            <Typography
              variant="h6"
              sx={{
                fontWeight: 700,
                color: "#111b21",
                mb: 0.5,
                fontSize: "18px",
              }}
            >
              Configurar Parâmetros
            </Typography>
            <Typography
              variant="body2"
              sx={{
                color: "#667781",
                fontSize: "14px",
                fontWeight: 500,
                mb: 1,
              }}
            >
              {template.name}
            </Typography>
          </Box>
        </Box>
      </DialogTitle>
      <DialogContent sx={{ p: 3, pt: 4 }}>
        {loading ? (
          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              py: 6,
              flexDirection: "column",
              gap: 2,
            }}
          >
            <CircularProgress size={40} sx={{ color: "#00a884" }} />
            <Typography
              variant="body1"
              sx={{ color: "#667781", fontWeight: 500 }}
            >
              Carregando parâmetros do template...
            </Typography>
          </Box>
        ) : templateParameters ? (
          <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
            {/* Layout de Duas Colunas */}
            {templateParameters.requiredParametersCount > 0 && (
              <Box sx={{ display: "flex", gap: 2, mt: 1.5 }}>
                {/* Coluna da Esquerda - Configurações */}
                <Box sx={{ flex: 1 }}>
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: 1,
                      mb: 1.5,
                    }}
                  >
                    <SettingsIcon sx={{ color: "#00a884", fontSize: 18 }} />
                    <Typography
                      variant="subtitle1"
                      sx={{
                        fontWeight: 600,
                        color: "#111b21",
                        fontSize: "14px",
                      }}
                    >
                      Configuração de Parâmetros
                    </Typography>
                    <Chip
                      label={`${parameterConfigs.filter((config) => isParameterConfigured(config)).length}/${parameterConfigs.length}`}
                      size="small"
                      color="success"
                      sx={{ fontSize: "10px", height: "20px" }}
                    />
                    {templateParameters && (
                      <Chip
                        label={`${templateParameters.requiredParametersCount} necessário${templateParameters.requiredParametersCount !== 1 ? "s" : ""}`}
                        size="small"
                        color="primary"
                        sx={{ fontSize: "10px", height: "20px" }}
                      />
                    )}
                  </Box>

                  <Stack spacing={1.5}>
                    {parameterConfigs.map((config, index) => {
                      const validation = getValidationStatus(config);
                      return (
                        <Card
                          key={index}
                          sx={{
                            borderRadius: "8px",
                            border: validation.valid
                              ? "1px solid #e8f5e8"
                              : "1px solid #fff3cd",
                            backgroundColor: validation.valid
                              ? "#f8fff8"
                              : "#fffef7",
                            boxShadow: "0 1px 4px rgba(0, 0, 0, 0.06)",
                            transition: "all 0.2s ease",
                            "&:hover": {
                              boxShadow: "0 2px 8px rgba(0, 0, 0, 0.1)",
                              transform: "translateY(-1px)",
                            },
                          }}
                        >
                          <CardContent sx={{ p: 1.5 }}>
                            {/* Header do parâmetro */}
                            <Box
                              sx={{
                                display: "flex",
                                justifyContent: "space-between",
                                alignItems: "center",
                                mb: 1,
                              }}
                            >
                              <Box
                                sx={{
                                  display: "flex",
                                  alignItems: "center",
                                  gap: 1,
                                }}
                              >
                                <Box
                                  sx={{
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "center",
                                    width: 24,
                                    height: 24,
                                    borderRadius: "6px",
                                    backgroundColor: validation.valid
                                      ? "#e8f5e8"
                                      : "#fff3cd",
                                    color: validation.valid
                                      ? "#2e7d32"
                                      : "#f57c00",
                                  }}
                                >
                                  {validation.valid ? (
                                    <CheckCircleIcon sx={{ fontSize: 14 }} />
                                  ) : (
                                    <WarningIcon sx={{ fontSize: 14 }} />
                                  )}
                                </Box>
                                <Box>
                                  <Typography
                                    variant="subtitle2"
                                    sx={{
                                      fontWeight: 600,
                                      color: "#111b21",
                                      fontSize: "12px",
                                    }}
                                  >
                                    Parâmetro {config.index + 1}
                                  </Typography>
                                  <Typography
                                    variant="caption"
                                    sx={{
                                      color: "#667781",
                                      fontSize: "10px",
                                    }}
                                  >
                                    Texto
                                  </Typography>
                                </Box>
                              </Box>
                              <Chip
                                label={validation.message}
                                size="small"
                                color={validation.valid ? "success" : "warning"}
                                sx={{ fontSize: "9px", height: "18px" }}
                              />
                            </Box>

                            <Divider sx={{ mb: 1, opacity: 0.3 }} />

                            {/* Configurações */}
                            <Box
                              sx={{
                                display: "flex",
                                gap: 1.5,
                                alignItems: "flex-start",
                              }}
                            >
                              <FormControl size="small" sx={{ flex: 1 }}>
                                <InputLabel sx={{ fontSize: "11px" }}>
                                  Tipo
                                </InputLabel>
                                <Select
                                  value={config.type}
                                  onChange={(e) =>
                                    handleParameterChange(
                                      index,
                                      "type",
                                      e.target.value,
                                    )
                                  }
                                  label="Tipo"
                                  sx={{
                                    fontSize: "12px",
                                    height: "32px",
                                    "& .MuiSelect-select": {
                                      fontSize: "12px",
                                      padding: "6px 12px",
                                    },
                                  }}
                                >
                                  <MenuItem
                                    value="fixed"
                                    sx={{ fontSize: "12px" }}
                                  >
                                    <Box
                                      sx={{
                                        display: "flex",
                                        alignItems: "center",
                                        gap: 0.5,
                                      }}
                                    >
                                      <TextFieldsIcon sx={{ fontSize: 14 }} />
                                      Valor Fixo
                                    </Box>
                                  </MenuItem>
                                  <MenuItem
                                    value="dynamic"
                                    sx={{ fontSize: "12px" }}
                                  >
                                    <Box
                                      sx={{
                                        display: "flex",
                                        alignItems: "center",
                                        gap: 0.5,
                                      }}
                                    >
                                      <PersonIcon sx={{ fontSize: 14 }} />
                                      Dados Dinâmicos
                                    </Box>
                                  </MenuItem>
                                </Select>
                              </FormControl>

                              {config.type === "fixed" ? (
                                <TextField
                                  size="small"
                                  label="Valor Fixo"
                                  placeholder="Digite o valor fixo"
                                  value={config.value || ""}
                                  onChange={(e) =>
                                    handleParameterChange(
                                      index,
                                      "value",
                                      e.target.value,
                                    )
                                  }
                                  InputProps={{
                                    startAdornment: (
                                      <InputAdornment position="start">
                                        <TextFieldsIcon
                                          sx={{
                                            fontSize: 14,
                                            color: "#667781",
                                          }}
                                        />
                                      </InputAdornment>
                                    ),
                                  }}
                                  sx={{
                                    flex: 1,
                                    "& .MuiInputBase-root": {
                                      height: "32px",
                                    },
                                    "& .MuiInputLabel-root": {
                                      fontSize: "11px",
                                    },
                                    "& .MuiInputBase-input": {
                                      fontSize: "12px",
                                      padding: "6px 12px",
                                    },
                                  }}
                                />
                              ) : (
                                <FormControl size="small" sx={{ flex: 1 }}>
                                  <InputLabel sx={{ fontSize: "11px" }}>
                                    Campo Dinâmico
                                  </InputLabel>
                                  <Select
                                    value={config.dynamicField ?? "clientName"}
                                    onChange={(e) =>
                                      handleParameterChange(
                                        index,
                                        "dynamicField",
                                        e.target.value,
                                      )
                                    }
                                    label="Campo Dinâmico"
                                    sx={{
                                      fontSize: "12px",
                                      height: "32px",
                                      "& .MuiSelect-select": {
                                        fontSize: "12px",
                                        padding: "6px 12px",
                                      },
                                    }}
                                  >
                                    <MenuItem
                                      value="clientName"
                                      sx={{ fontSize: "12px" }}
                                    >
                                      <Box
                                        sx={{
                                          display: "flex",
                                          alignItems: "center",
                                          gap: 0.5,
                                        }}
                                      >
                                        <PersonIcon sx={{ fontSize: 14 }} />
                                        Nome do Cliente
                                      </Box>
                                    </MenuItem>
                                    <MenuItem
                                      value="clientPhone"
                                      sx={{ fontSize: "12px" }}
                                    >
                                      <Box
                                        sx={{
                                          display: "flex",
                                          alignItems: "center",
                                          gap: 0.5,
                                        }}
                                      >
                                        <PhoneIcon sx={{ fontSize: 14 }} />
                                        Telefone do Cliente
                                      </Box>
                                    </MenuItem>
                                    <MenuItem
                                      value="companyName"
                                      sx={{ fontSize: "12px" }}
                                    >
                                      <Box
                                        sx={{
                                          display: "flex",
                                          alignItems: "center",
                                          gap: 0.5,
                                        }}
                                      >
                                        <BusinessIcon sx={{ fontSize: 14 }} />
                                        Nome da Empresa
                                      </Box>
                                    </MenuItem>
                                    <MenuItem
                                      value="attendantName"
                                      sx={{ fontSize: "12px" }}
                                    >
                                      <Box
                                        sx={{
                                          display: "flex",
                                          alignItems: "center",
                                          gap: 0.5,
                                        }}
                                      >
                                        <PersonIcon sx={{ fontSize: 14 }} />
                                        Nome do Atendente
                                      </Box>
                                    </MenuItem>
                                    <MenuItem
                                      value="scheduledDate"
                                      sx={{ fontSize: "12px" }}
                                    >
                                      <Box
                                        sx={{
                                          display: "flex",
                                          alignItems: "center",
                                          gap: 0.5,
                                        }}
                                      >
                                        <ScheduleIcon sx={{ fontSize: 14 }} />
                                        Data do Agendamento
                                      </Box>
                                    </MenuItem>
                                  </Select>
                                </FormControl>
                              )}
                            </Box>
                          </CardContent>
                        </Card>
                      );
                    })}
                  </Stack>
                </Box>

                {/* Coluna da Direita - Preview */}
                <Box sx={{ flex: 1 }}>
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: 1,
                      mb: 1.5,
                    }}
                  >
                    <TextFieldsIcon sx={{ color: "#00a884", fontSize: 18 }} />
                    <Typography
                      variant="subtitle1"
                      sx={{
                        fontWeight: 600,
                        color: "#111b21",
                        fontSize: "14px",
                      }}
                    >
                      Preview do Template
                    </Typography>
                  </Box>

                  <Card
                    sx={{
                      borderRadius: "8px",
                      border: "1px solid #e9edef",
                      boxShadow: "0 1px 4px rgba(0, 0, 0, 0.06)",
                    }}
                  >
                    <TemplatePreview
                      selectedTemplate={template}
                      templateParameters={templateParameters}
                      parameterConfigs={parameterConfigs}
                      attendantName="Atendente Exemplo"
                    />
                  </Card>
                </Box>
              </Box>
            )}
          </Box>
        ) : (
          <Alert severity="warning">
            <Typography variant="body2">
              Não foi possível carregar as informações dos parâmetros do
              template.
            </Typography>
          </Alert>
        )}
      </DialogContent>
      <DialogActions
        sx={{
          p: 2,
          borderTop: "1px solid #e9edef",
          background: "linear-gradient(135deg, #f8f9fa 0%, #ffffff 100%)",
          justifyContent: "center",
          gap: 2,
        }}
      >
        <Button
          onClick={onClose}
          disabled={saving}
          sx={{
            borderRadius: "8px",
            textTransform: "none",
            fontWeight: 600,
            px: 4,
            py: 1.2,
            fontSize: "13px",
            color: "#667781",
            border: "1px solid #e9edef",
            backgroundColor: "#fff",
            "&:hover": {
              backgroundColor: "#f0f2f5",
              borderColor: "#d1d7db",
            },
            "&:disabled": {
              opacity: 0.6,
              cursor: "not-allowed",
            },
            transition: "all 0.2s cubic-bezier(0.4, 0, 0.2, 1)",
          }}
        >
          Cancelar
        </Button>
        <Button
          onClick={handleSave}
          variant="contained"
          disabled={saving || loading}
          startIcon={
            saving ? (
              <CircularProgress size={16} sx={{ color: "white" }} />
            ) : (
              <SaveIcon />
            )
          }
          sx={{
            borderRadius: "8px",
            textTransform: "none",
            fontWeight: 600,
            px: 4,
            py: 1.2,
            fontSize: "13px",
            backgroundColor: "#00a884",
            boxShadow: "0 2px 8px rgba(0, 168, 132, 0.3)",
            "&:hover": {
              backgroundColor: "#008f6f",
              boxShadow: "0 4px 12px rgba(0, 168, 132, 0.4)",
              transform: "translateY(-1px)",
            },
            "&:disabled": {
              backgroundColor: "#ccc",
              boxShadow: "none",
              transform: "none",
            },
            transition: "all 0.2s cubic-bezier(0.4, 0, 0.2, 1)",
          }}
        >
          {saving ? "Salvando..." : "Salvar Configuração"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default TemplateParameterConfigurator;
